/**
 * Product analytics (PostHog, loaded by /ph.js). Only named milestones with no content: never terminal output, input,
 * prompts, computer addresses, keys or pairing codes. Safe to call when PostHog is blocked or absent.
 *
 * Event names (shared with the relay's `computer_linked`, see docs/admin-analytics-and-monitoring.md):
 *   pairing_started, pairing_succeeded, connection_failed {stage, reason}, connection_stale (looked connected, computer silent), connection_recovered, session_connected,
 *   agent_session_started {agent, how}, remote_action_succeeded {action}, remote_action_failed {action, reason},
 *   first_remote_action {action} (activation, once per browser), app_error {kind}.
 */
import type { Hub } from "@foreman/app-core";
import { reportConnection } from "./connection-report";

type PH = { capture: (name: string, props?: Record<string, unknown>) => void; identify: (id: string) => void; reset: () => void; get_distinct_id?: () => string };
const ph = () => (window as unknown as { posthog?: PH }).posthog;

export const track = (name: string, props?: Record<string, unknown>): void => {
  try {
    ph()?.capture(name, props);
  } catch {
    /* analytics must never break the app */
  }
};

/** Once per page load for a given key (a screen re-rendering, a reconnect or a retry must not send it again). */
const seen = new Set<string>();
export function trackOnce(key: string, name: string, props?: Record<string, unknown>): void {
  if (seen.has(key)) return;
  seen.add(key);
  track(name, props);
}

const ACTIVATED_KEY = "bc.activated";
let activatedInMemory = false;
const activatedBefore = (): boolean => {
  if (activatedInMemory) return true;
  try {
    return localStorage.getItem(ACTIVATED_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * A remote action that really succeeded: an answer the computer accepted, a git change it made, or input it relayed and the
 * session answered. `scope` limits repeats (a session id, or an operation id); the first ever one also sends the activation event.
 */
export function remoteActionSucceeded(action: "terminal_input" | "approval" | "answer" | "takeover" | "git", scope?: string, ms?: number): void {
  if (scope && seen.has(`ra:${action}:${scope}`)) return;
  if (scope) seen.add(`ra:${action}:${scope}`);
  track("remote_action_succeeded", { action, ...(ms !== undefined && { duration_ms: Math.round(ms) }) });
  noteUse();
  if (activatedBefore()) return;
  activatedInMemory = true;
  try {
    localStorage.setItem(ACTIVATED_KEY, "1");
  } catch {
    /* private mode: once per page load */
  }
  track("first_remote_action", { action });
}

let endedSession = false;
/** A session was ended or removed on this phone. If a guest then starts another, that is a swap: someone using Brocoded without signing up. */
export const noteSessionEnded = (): void => {
  endedSession = true;
};
export function trackSessionStarted(props: { agent: string; how: string }, plan?: string): void {
  track("agent_session_started", { ...props, plan: plan ?? "unknown" });
  if (plan === "guest" && endedSession) {
    endedSession = false;
    track("guest_session_swapped");
  }
}

/** `reason` must be a short category (never an error message). Anything that isn't a plain word becomes "error". */
export const safeReason = (r: unknown): string => (typeof r === "string" && /^[a-z][a-z0-9_-]{0,31}$/.test(r) ? r : "error");

export function remoteActionFailed(action: string, reason: unknown, ms?: number): void {
  track("remote_action_failed", { action, reason: safeReason(reason), ...(ms !== undefined && { duration_ms: Math.round(ms) }) });
}

/** An approval or question answered: how long it waited for a person (`wait_s`), what they chose, and whether the computer took it. */
export function approvalDecided(p: { decision: "approve" | "deny" | "answer"; createdAt: number; ok: boolean; ms: number }): void {
  const wait = (Date.now() - p.createdAt) / 1000;
  track("approval_decided", { decision: p.decision, ok: p.ok, duration_ms: Math.round(p.ms), ...(wait >= 0 && wait < 7 * 86400 && { wait_s: Math.round(wait) }) });
}

/** A tapped alert (a push or a heads-up) that brought the person to the app, by the alert's kind only. */
export const alertOpened = (kind?: string): void => track("alert_opened", { kind: safeReason(kind ?? "unknown") });

const USES_KEY = "bc.uses";
/** Successful remote actions on this browser, ever; the review prompt waits for a few. */
export function useCount(): number {
  try {
    return Number(localStorage.getItem(USES_KEY)) || 0;
  } catch {
    return 0;
  }
}
function noteUse(): void {
  try {
    localStorage.setItem(USES_KEY, String(useCount() + 1));
  } catch {
    /* private mode: no count, so no prompt */
  }
}

/** Coarse platform from the user agent, never the agent string itself. */
export function platformOf(): { platform: "ios" | "android" | "mac" | "windows" | "other"; installed: boolean } {
  const ua = navigator.userAgent;
  const platform = /iPhone|iPad|iPod/.test(ua) ? "ios" : /Android/.test(ua) ? "android" : /Mac/.test(ua) ? "mac" : /Windows/.test(ua) ? "windows" : "other";
  const installed = matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  return { platform, installed };
}

/** Once per page load: the base for daily / weekly active people and day-1 / day-7 retention. */
export function trackAppOpened(): void {
  trackOnce("app_opened", "app_opened");
}

/** Which agent a session runs, from a fixed list, never the command line. */
export const agentLabel = (key: string): "claude" | "codex" | "antigravity" | "other" => (key === "claude" || key === "codex" || key === "antigravity" ? key : "other");

/**
 * Watches every computer's connection and reports the moments that matter: it could not be reached or was refused
 * (once per drop, not per retry) and it came back. Returns a stop function.
 */
export function watchConnections(hub: Hub): () => void {
  const last = new Map<string, string>();
  const lastStale = new Map<string, number>();
  const everReady = new Set<string>();
  const droppedAt = new Map<string, number>();
  const check = () => {
    for (const s of hub.getSnapshot()) {
      const id = s.computer.id;
      // The link looked up but the computer had stopped answering; the app noticed and redialled (see checkLink in app-core).
      const stale = hub.get(id)?.getSnapshot().staleAt ?? null;
      if (stale && stale !== lastStale.get(id)) {
        lastStale.set(id, stale);
        track("connection_stale", { had_connected: everReady.has(id) });
        reportConnection("stale", "silent");
        if (!droppedAt.has(id)) droppedAt.set(id, Date.now());
      }
      const prev = last.get(id);
      const now = s.state;
      if (prev === now) continue;
      last.set(id, now);
      if (prev === undefined && now !== "offline" && now !== "unauthorized") {
        if (now === "ready") everReady.add(id);
        continue;
      }
      if (now === "ready") {
        const since = droppedAt.get(id);
        if (everReady.has(id) && prev !== undefined) {
          track("connection_recovered", since === undefined ? {} : { after_s: Math.round((Date.now() - since) / 1000) });
          reportConnection("recovered", "reconnected");
        }
        droppedAt.delete(id);
        everReady.add(id);
      } else if (now === "offline" || now === "unauthorized") {
        track("connection_failed", { stage: "connect", reason: now, had_connected: everReady.has(id) });
        reportConnection(now === "offline" ? "offline" : "unauthorized", everReady.has(id) ? "after_connected" : "never_connected");
        if (!droppedAt.has(id)) droppedAt.set(id, Date.now());
      }
    }
  };
  const off = hub.subscribe(check);
  check();
  return off;
}

/**
 * Why the sign-in prompt is showing (second_session, import, git, history, second_computer). Kept while the prompt is open so the
 * sign-in events it leads to carry the same `trigger`.
 */
let signInTrigger: string | undefined;
export const setSignInTrigger = (t: string | undefined): void => {
  signInTrigger = t;
};
export const signInProps = (extra?: Record<string, unknown>): Record<string, unknown> => ({ ...(signInTrigger ? { trigger: signInTrigger } : {}), ...extra });

/** Sign-in failures as a short category, never the message. */
export const signInFailureReason = (message: string): string =>
  /suspend/i.test(message) ? "suspended" : /reach|connection/i.test(message) ? "network" : /verif|token/i.test(message) ? "bad_token" : /too many/i.test(message) ? "rate_limited" : "error";

/** Ties this browser's events to the account's pseudonymous id (called when signed in). Never pass an email or name. */
export const identify = (analyticsId: string): void => {
  try {
    const p = ph();
    if (p && p.get_distinct_id?.() !== analyticsId) p.identify(analyticsId);
  } catch {
    /* analytics must never break the app */
  }
};

/** Signed out: the next visitor on this browser starts as a new anonymous person. */
export const resetIdentity = (): void => {
  try {
    ph()?.reset();
  } catch {
    /* analytics must never break the app */
  }
};

/** Every event carries the app version and coarse platform, so errors and reliability can be compared across releases. */
export function registerVersion(): void {
  try {
    const v = String(import.meta.env.VITE_APP_VERSION ?? "dev");
    (window as unknown as { posthog?: { register: (p: Record<string, unknown>) => void } }).posthog?.register({ app_version: v, ...platformOf() });
  } catch {
    /* analytics must never break the app */
  }
}
